import { create } from 'zustand'
import {
  type BrandingSettings,
  getSettings,
  type SettingsResponse,
  type TenantInfo,
} from '@/api/settings'
import i18n from '@/i18n'
import { useAuthStore } from './auth'

interface SettingsState {
  tenant: TenantInfo | null
  branding: BrandingSettings
  loading: boolean
  error: string | null
  initialized: boolean
  fetchSettings: () => Promise<void>

  // Tenant getters
  getCurrency: () => string

  // Branding getters
  getPlatformName: () => string
  getCompanyName: () => string
  getLogoUrl: () => string | undefined
  getFaviconUrl: () => string | undefined
  getPrimaryColor: () => string
  getAdminEmail: () => string | undefined
  getSupportEmail: () => string | undefined
  getSupportUrl: () => string | undefined
  getTimezone: () => string
  getLanguage: () => string
}

const DEFAULT_PRIMARY_COLOR = '#2563eb'
const DEFAULT_TIMEZONE = 'UTC'
const DEFAULT_LANGUAGE = 'en-US'
const SUPPORTED_LANGUAGES = ['en-US', 'zh-CN', 'zh-HK']

// LocalStorage key for branding cache (persists across logout for white-label continuity)
// IMPORTANT: The primary_color key must match the one in index.html pre-load script
const STORAGE_KEY_PRIMARY_COLOR = 'orax_primary_color'
const STORAGE_KEY_BRANDING = 'orax_branding'

// Color validation regex (6-char hex only, e.g., #1677ff)
const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/

function isValidHexColor(color: unknown): color is string {
  return typeof color === 'string' && HEX_COLOR_REGEX.test(color)
}

// --- Branding cache (localStorage) ---
// Cached branding survives logout so the login page still shows the tenant's brand.
// On next login, fetchSettings overwrites the cache with the new tenant's data.

function getCachedBranding(): BrandingSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_BRANDING)
    if (raw) {
      return JSON.parse(raw) as BrandingSettings
    }
  } catch {
    // corrupt or unavailable
  }
  return {}
}

function cacheBranding(branding: BrandingSettings) {
  try {
    localStorage.setItem(STORAGE_KEY_BRANDING, JSON.stringify(branding))
    // Keep primary_color in its own key for the index.html pre-load script
    const color = branding.primary_color
    if (isValidHexColor(color)) {
      localStorage.setItem(STORAGE_KEY_PRIMARY_COLOR, color)
    } else {
      // Clear stale color when branding has no valid primary_color
      localStorage.removeItem(STORAGE_KEY_PRIMARY_COLOR)
    }
  } catch {
    // localStorage not available
  }
}

const DEFAULT_CURRENCY = 'USD'

// Initialize branding from cache so the UI never flashes default values
const cachedBranding = getCachedBranding()

export const useSettingsStore = create<SettingsState>((set, get) => ({
  tenant: null,
  branding: cachedBranding,
  loading: false,
  error: null,
  initialized: false,

  fetchSettings: async () => {
    set({ loading: true, error: null })
    try {
      const response = await getSettings()
      const data: SettingsResponse = response?.data ?? {}

      // Use branding if available, fallback to platform (legacy)
      const branding = data?.branding || data?.platform || {}
      const tenant = data?.tenant || null

      set({
        tenant,
        branding,
        loading: false,
        initialized: true,
      })

      // Persist branding to localStorage for cross-session continuity
      cacheBranding(branding)

      // Apply favicon if set (with security validation)
      const faviconUrl = branding.favicon_url
      if (isValidAssetUrl(faviconUrl)) {
        applyFavicon(faviconUrl)
      }

      // Apply document title if set
      const platformName = branding.platform_name as string | undefined
      if (platformName) {
        document.title = platformName
      }

      // Apply language setting and sync with i18n
      // Priority: User preference → Tenant setting → Default
      const user = useAuthStore.getState().user
      const userLang = user?.language
      const tenantLang = branding.language as string | undefined
      const effectiveLang =
        userLang && SUPPORTED_LANGUAGES.includes(userLang)
          ? userLang
          : tenantLang && SUPPORTED_LANGUAGES.includes(tenantLang)
            ? tenantLang
            : null

      if (effectiveLang && i18n.language !== effectiveLang) {
        i18n.changeLanguage(effectiveLang)
      }
    } catch (_error) {
      // Clear branding on failure to prevent cross-tenant leakage.
      // If the API is down after switching accounts, the user should see
      // default branding, not the previous tenant's.
      try {
        localStorage.removeItem(STORAGE_KEY_BRANDING)
        localStorage.removeItem(STORAGE_KEY_PRIMARY_COLOR)
      } catch {
        // localStorage not available
      }
      set({
        branding: {},
        tenant: null,
        error: 'Failed to load settings',
        loading: false,
        initialized: true,
      })
    }
  },

  getCurrency: () => {
    const { tenant } = get()
    return tenant?.currency || DEFAULT_CURRENCY
  },

  getPlatformName: () => {
    const { branding } = get()
    return (branding.platform_name as string) || ''
  },

  getCompanyName: () => {
    const { branding } = get()
    return (branding.company_name as string) || ''
  },

  getLogoUrl: () => {
    const { branding } = get()
    return branding.logo_url as string | undefined
  },

  getFaviconUrl: () => {
    const { branding } = get()
    return branding.favicon_url as string | undefined
  },

  getPrimaryColor: () => {
    const { branding } = get()
    const color = branding.primary_color
    if (isValidHexColor(color)) {
      return color
    }
    return DEFAULT_PRIMARY_COLOR
  },

  getAdminEmail: () => {
    const { branding } = get()
    return branding.admin_email as string | undefined
  },

  getSupportEmail: () => {
    const { branding } = get()
    return branding.support_email as string | undefined
  },

  getSupportUrl: () => {
    const { branding } = get()
    return branding.support_url as string | undefined
  },

  // Fallback: User preference → Tenant setting → Default
  getTimezone: () => {
    // Check user preference first
    const user = useAuthStore.getState().user
    if (user?.timezone) {
      return user.timezone
    }
    // Fall back to tenant setting
    const { branding } = get()
    return (branding.timezone as string) || DEFAULT_TIMEZONE
  },

  // Fallback: User preference → Tenant setting → Default
  getLanguage: () => {
    // Check user preference first
    const user = useAuthStore.getState().user
    if (user?.language && SUPPORTED_LANGUAGES.includes(user.language)) {
      return user.language
    }
    // Fall back to tenant setting
    const { branding } = get()
    const lang = branding.language as string
    if (lang && SUPPORTED_LANGUAGES.includes(lang)) {
      return lang
    }
    return DEFAULT_LANGUAGE
  },
}))

// Validate URL for security (only allow http/https and data URIs)
function isValidAssetUrl(url: unknown): url is string {
  if (typeof url !== 'string' || !url) return false
  try {
    // Allow data URIs for inline images
    if (url.startsWith('data:image/')) return true
    // Only allow http/https URLs
    const parsed = new URL(url)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

// Helper to apply favicon dynamically
function applyFavicon(url: string) {
  // Security: validate URL before applying
  if (!isValidAssetUrl(url)) return

  let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']")
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  link.href = url
}

// Called on logout: reset initialized flag so fetchSettings runs on next login,
// but keep branding in localStorage so login page retains the tenant's look.
export function clearThemeCache() {
  useSettingsStore.setState({
    tenant: null,
    initialized: false,
    error: null,
    // Keep branding from cache — login page still shows tenant brand
    branding: getCachedBranding(),
  })
}
